import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Boxes, FileCheck2, FileText, LayoutDashboard, Plus, ReceiptIndianRupee,
  Settings, Truck, UserCheck, UserCog, Users, User, Wallet,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";

const NAV = [
  { label: "Dashboard", to: "/app/dashboard", icon: LayoutDashboard, module: "dashboard" },
  { label: "CRM / Leads", to: "/app/crm", icon: Users, module: "crm" },
  { label: "Customers", to: "/app/customers", icon: UserCheck, module: "customers" },
  { label: "Quotations", to: "/app/quotations", icon: FileText, module: "quotations" },
  { label: "Proforma Invoices", to: "/app/proformas", icon: FileCheck2, module: "proformas" },
  { label: "Invoices", to: "/app/invoices", icon: ReceiptIndianRupee, module: "invoices" },
  { label: "Receivables", to: "/app/receivables", icon: Wallet, module: "invoices" },
  { label: "Purchase Orders", to: "/app/purchase-orders", icon: Truck, module: "purchase_orders" },
  { label: "Inventory", to: "/app/inventory", icon: Boxes, module: "inventory" },
  { label: "My Profile", to: "/app/profile", icon: User, module: "dashboard" },
];

const ADMIN_NAV = [
  { label: "Analytics", to: "/app/analytics", icon: BarChart3 },
  { label: "Staff Management", to: "/app/staff", icon: UserCog },
  { label: "Admin Settings", to: "/app/settings", icon: Settings },
];

export const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.results || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (to) => { onOpenChange(false); setQ(""); navigate(to); };

  const actions = [
    { label: "Create a new lead", to: "/app/crm?new=1", module: "crm", action: "add" },
    { label: "Create a new quotation", to: "/app/quotations?new=1", module: "quotations", action: "add" },
    { label: "Create a new invoice", to: "/app/invoices?new=1", module: "invoices", action: "add" },
    { label: "Create a purchase order", to: "/app/purchase-orders?new=1", module: "purchase_orders", action: "add" },
    { label: "Open today's follow-ups", to: "/app/crm?tab=followups&bucket=today", module: "crm", action: "view" },
    { label: "Open overdue follow-ups", to: "/app/crm?tab=followups&bucket=overdue", module: "crm", action: "view" },
  ].filter((a) => can(a.module, a.action));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search or jump to..." value={q} onValueChange={setQ} data-testid="palette-input" />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>Nothing found. Try a name, company, phone or document number.</CommandEmpty>

        {results.length > 0 && (
          <>
            <CommandGroup heading="Records">
              {results.map((r, i) => (
                <CommandItem key={`r-${i}`} value={`${r.type} ${r.title} ${r.subtitle}`} onSelect={() => go(r.link)}>
                  <span className="mr-2 rounded bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">{r.type}</span>
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="ml-2 truncate text-xs text-muted-foreground">{r.subtitle}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {actions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {actions.map((a) => (
              <CommandItem key={a.to} value={a.label} onSelect={() => go(a.to)}>
                <Plus className="mr-2 h-4 w-4 text-primary" /> {a.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Go to">
          {NAV.filter((n) => can(n.module, "view")).map((n) => (
            <CommandItem key={n.to} value={n.label} onSelect={() => go(n.to)}>
              <n.icon className="mr-2 h-4 w-4" /> {n.label}
            </CommandItem>
          ))}
          {isAdmin && ADMIN_NAV.map((n) => (
            <CommandItem key={n.to} value={n.label} onSelect={() => go(n.to)}>
              <n.icon className="mr-2 h-4 w-4" /> {n.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
